import React, { useState } from 'react';

import TextInput from 'common/inputs/TextInput';
import ModernModal from 'common/modals/ModernModal';
import ButtonV2 from 'common/ui/ButtonV2';
import TextareaV2 from 'common/ui/TextareaV2';
import VStack from 'common/uiV3/VStack';
import validateInput from 'common/validateInput';

import type { IdeaTheme } from 'common/api/endpoints/idea';
import type { IdeaFieldTypes } from 'common/constants/ideas';

type BaseFieldModalProps = {
  loading: boolean;
  onClose: () => void;
  onSubmit: (theme: IdeaTheme) => void;
  types?: IdeaFieldTypes[];
};

type CreateFieldModalProps = BaseFieldModalProps & {
  mode: 'create';
  initialData?: never;
};

type UpdateFieldModalProps = BaseFieldModalProps & {
  mode: 'update';
  initialData: IdeaTheme;
};

type Props = CreateFieldModalProps | UpdateFieldModalProps;

type FormFieldProps = {
  label: string;
  required?: boolean;
  children: React.ReactNode;
};

const useThemeForm = (initialData?: IdeaTheme) => {
  const [formState, setFormState] = useState<IdeaTheme>(
    initialData ?? {
      _id: '',
      autopilotEnabled: false,
      created: new Date().toISOString(),
      createdByAutopilot: false,
      description: '',
      name: '',
    },
  );

  const setField = <T extends keyof IdeaTheme>(field: T, value: IdeaTheme[T]) => {
    setFormState((currentState) => ({ ...currentState, [field]: value }));
  };

  const isValid =
    validateInput.themes.name(formState?.name ?? '') &&
    validateInput.themes.description(formState?.description ?? '');

  return [formState, setField, isValid] as const;
};

const FormField = ({ label, required, children }: FormFieldProps) => {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-semibold">
        {label} {required && <span className="text-tomato-90">*</span>}
      </label>
      {children}
    </div>
  );
};

export default function AdminV2IdeaThemeModal(props: Props) {
  const { onClose, loading, mode, onSubmit, initialData } = props;
  const [formState, setFormState, isValid] = useThemeForm(initialData);

  const headerText = mode === 'create' ? 'Create new theme' : 'Edit theme';
  return (
    <ModernModal
      modalClassName="w-[500px]"
      header={headerText}
      onClose={onClose}
      sections={[
        <VStack key="main" className="gap-4">
          <FormField label="Theme name" required>
            <TextInput
              autoFocus
              onChange={(e) => setFormState('name', e.target.value)}
              placeholder="Billing & Payments"
              value={formState?.name ?? ''}
            />
          </FormField>
          <FormField label="Theme description">
            <TextareaV2
              autoResize
              autoResizeMaxRows={5}
              onChange={(e) => setFormState('description', e.target.value)}
              placeholder="A theme for ideas related to invoicing, charges, paywalls, ..."
              value={formState?.description ?? ''}
            />
          </FormField>
        </VStack>,
      ]}
      footer={
        <>
          <ButtonV2 disabled={loading} variant="outlined" size="medium" onClick={onClose}>
            Cancel
          </ButtonV2>
          <ButtonV2
            disabled={loading || !isValid}
            size="medium"
            onClick={() => onSubmit(formState)}
            loading={loading}>
            {mode === 'create' ? 'Create' : 'Save'}
          </ButtonV2>
        </>
      }
    />
  );
}
